import { useState, useEffect, useCallback } from 'react';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  getAdditionalUserInfo as firebaseGetAdditionalUserInfo,
  onIdTokenChanged as firebaseOnIdTokenChanged,
  UserCredential,
} from 'firebase/auth';
import { log } from '@/logger/logger.ts';
import { auth } from '../../firebase';
import {
  initiateFigmaRedirectSignIn,
  signInWithAuthorizationCode,
  getStoredPromotion,
  clearOidcAccessToken,
} from './oidc-providers/figma-oidc-provider';
import { INTERNAL_AUTH_ERROR_CODES, InternalAuthError } from './firebase-errors';
import type { AuthUser } from '@/types/auth.types';
import type { AuthProviderHook, SignInResult } from './../authProvider.types';

const logger = log.getLogger('useFirebaseAuthProvider');

/**
 * How long the app will wait for the identity provider to say who is here before rendering as
 * signed out.
 *
 * `onAuthStateChanged` is the only thing that ever ends the full-screen loader, and it has no
 * failure mode of its own: if it never fires, nothing renders, no route mounts, and a visitor sees
 * a spinner with no way forward — not even the sign-in screen a signed-out visitor is owed. Long
 * enough that a provider merely being slow is not mistaken for one that is never going to answer.
 */
const AUTH_STATE_DEADLINE_MS = 8 * 1000;

export const useFirebaseAuthProvider = (): AuthProviderHook => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState<string | null>(null);

  const updateSessionToken = useCallback(async (firebaseUser) => {
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken();
        setSessionToken(token);
      } catch (error) {
        logger.error('Error getting ID token:', error);
        setSessionToken(null);
      }
    } else {
      setSessionToken(null);
    }
  }, []);

  // handle user state changes
  useEffect(() => {
    let hasAnswered = false;
    // Gives up waiting rather than holding the whole app on a loader. A later answer is still
    // honoured: this only stops blocking, it does not sign anybody out.
    const deadline = setTimeout(() => {
      if (hasAnswered) {
        return;
      }
      logger.error('Identity provider never reported an auth state; rendering as signed out');
      setIsLoading(false);
    }, AUTH_STATE_DEADLINE_MS);

    const unsubscribe = firebaseOnAuthStateChanged(auth, (firebaseUser) => {
      hasAnswered = true;
      clearTimeout(deadline);
      setUser(firebaseUser);
      void updateSessionToken(firebaseUser);
      setIsLoading(false);
    });

    return () => {
      clearTimeout(deadline);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, []);

  // handle id token changes
  useEffect(() => {
    if (!user) {
      return;
    }

    const unsubscribe = firebaseOnIdTokenChanged(auth, (firebaseUser) => {
      void updateSessionToken(firebaseUser);
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [user]);

  const initiateRedirectSignIn = useCallback(async (provider, redirectPath: string) => {
    if (provider !== 'figma') {
      throw new Error('Unsupported provider');
    }
    await initiateFigmaRedirectSignIn(redirectPath);
  }, []);

  const signIn = useCallback(
    async (
      provider: 'google' | 'microsoft' | 'figma',
      options?: {
        hintEmail?: string;
        microsoftTenantId?: string;
        authorizationCode?: string;
      },
    ): Promise<SignInResult> => {
      // Handle Figma OAuth redirect flow
      if (provider === 'figma') {
        if (!options?.authorizationCode) {
          throw new InternalAuthError(
            INTERNAL_AUTH_ERROR_CODES.OIDC_MISSING_CODE,
            'Figma authorization code is required',
          );
        }

        return signInWithAuthorizationCode(options.authorizationCode);
      }

      // Handle Google and Microsoft popup flows
      let firebaseProvider: OAuthProvider | GoogleAuthProvider;
      const customParams: { tenant?: string; login_hint?: string } = {};

      if (provider === 'google') {
        firebaseProvider = new GoogleAuthProvider();
      } else if (provider === 'microsoft') {
        firebaseProvider = new OAuthProvider('microsoft.com');

        if (options?.microsoftTenantId) {
          customParams.tenant = options.microsoftTenantId;
        }
      } else {
        throw new InternalAuthError(INTERNAL_AUTH_ERROR_CODES.UNSUPPORTED_PROVIDER, 'Unsupported provider');
      }

      if (options?.hintEmail) {
        customParams.login_hint = options.hintEmail;
      }
      firebaseProvider.setCustomParameters(customParams);

      const result = await signInWithPopup(auth, firebaseProvider);

      // Return the result directly like the original FirebaseAuthProvider
      return result;
    },
    [],
  );

  const signOut = useCallback(async (): Promise<void> => {
    const result = await firebaseSignOut(auth);
    clearOidcAccessToken();
    return result;
  }, []);

  const getCurrentUser = useCallback(() => {
    return auth.currentUser;
  }, []);

  const getAdditionalUserInfo = useCallback((result: SignInResult) => {
    return firebaseGetAdditionalUserInfo(result as UserCredential);
  }, []);

  const getIdToken = useCallback(async (forceRefresh = false) => {
    return auth.currentUser?.getIdToken(forceRefresh) || null;
  }, []);

  return {
    signIn,
    signOut,
    initiateRedirectSignIn,
    getIdToken,
    getCurrentUser,
    getAdditionalUserInfo,
    getStoredPromotion,
    user,
    isLoading,
    accessToken: sessionToken,
  };
};
